import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, DollarSign, Clock, ArrowRight } from 'lucide-react';
import { translateProvider } from '@/utils/providerTranslations';

const categoryColors = {
  water: { bg: 'bg-gradient-to-br from-cyan-50 to-blue-50', text: 'text-cyan-700', border: 'border-cyan-200/50', icon: 'bg-cyan-500', gradient: 'from-cyan-500 to-blue-500' },
  electricity: { bg: 'bg-gradient-to-br from-amber-50 to-orange-50', text: 'text-amber-700', border: 'border-amber-200/50', icon: 'bg-amber-500', gradient: 'from-amber-500 to-orange-500' },
  arnona: { bg: 'bg-gradient-to-br from-purple-50 to-violet-50', text: 'text-purple-700', border: 'border-purple-200/50', icon: 'bg-purple-500', gradient: 'from-purple-500 to-violet-500' },
  fines: { bg: 'bg-gradient-to-br from-red-50 to-rose-50', text: 'text-red-700', border: 'border-red-200/50', icon: 'bg-red-500', gradient: 'from-red-500 to-rose-500' },
  education: { bg: 'bg-gradient-to-br from-emerald-50 to-green-50', text: 'text-emerald-700', border: 'border-emerald-200/50', icon: 'bg-emerald-500', gradient: 'from-emerald-500 to-green-500' },
  health: { bg: 'bg-gradient-to-br from-rose-50 to-pink-50', text: 'text-rose-700', border: 'border-rose-200/50', icon: 'bg-rose-500', gradient: 'from-rose-500 to-pink-500' },
  vaad_bayit: { bg: 'bg-gradient-to-br from-indigo-50 to-blue-50', text: 'text-indigo-700', border: 'border-indigo-200/50', icon: 'bg-indigo-500', gradient: 'from-indigo-500 to-blue-500' },
  general: { bg: 'bg-gradient-to-br from-slate-50 to-gray-50', text: 'text-slate-700', border: 'border-slate-200/50', icon: 'bg-slate-500', gradient: 'from-slate-500 to-gray-500' }
};

export default function BillCard({ bill, onClick }) {
  const { t, i18n } = useTranslation();
  const colors = categoryColors[bill.category] || categoryColors.general;
  const dueDate = new Date(bill.due_date);
  const today = new Date();
  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
  const isUrgent = daysUntilDue <= 7 && bill.status === 'unpaid';
  const isOverdue = daysUntilDue < 0 && bill.status === 'unpaid';

  return (
    <Card
      onClick={onClick}
      className={`relative overflow-hidden p-0 rounded-2xl border ${colors.border} ${colors.bg} cursor-pointer hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] group`}
      data-testid="bill-card"
    >
      {/* Top gradient bar */}
      <div className={`h-1.5 w-full bg-gradient-to-r ${colors.gradient}`} />
      
      <div className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between">
          <Badge className={`${colors.text} bg-white/80 backdrop-blur-sm text-xs font-semibold px-3 py-1 rounded-full shadow-sm`}>
            {t(bill.category)}
          </Badge>
          {isOverdue && (
            <Badge className="bg-red-500 text-white text-xs font-semibold px-3 py-1 rounded-full animate-pulse">
              {t('overdue')}
            </Badge>
          )}
          {isUrgent && !isOverdue && (
            <Badge className="bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
              <Clock className="h-3 w-3 mr-1" />
              {daysUntilDue}d
            </Badge>
          )}
        </div>

        {/* Provider & Description */}
        <div>
          <h3 className="text-xl font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors" data-testid="bill-card-provider">
            {translateProvider(bill.provider, i18n.language)}
          </h3>
          <p className="text-sm text-slate-500 line-clamp-1">{bill.description}</p>
        </div>

        {/* Amount & Date */}
        <div className="flex items-end justify-between pt-3 border-t border-slate-200/50">
          <div>
            <p className="text-xs text-slate-500 mb-1">{t('amount')}</p>
            <p className="text-2xl font-extrabold text-slate-900" data-testid="bill-card-amount">
              ₪{bill.amount.toLocaleString('en-IL', { minimumFractionDigits: 2 })}
            </p>
          </div>

          <div className="text-right">
            <p className="text-xs text-slate-500 mb-1">{t('due_date')}</p>
            <p className={`text-sm font-semibold ${isOverdue ? 'text-red-600' : isUrgent ? 'text-amber-600' : 'text-slate-700'}`}>
              {dueDate.toLocaleDateString('he-IL')}
            </p>
          </div>
        </div>

        {/* Status Badge */}
        <div className={`flex items-center justify-between p-3 rounded-xl ${
          bill.status === 'paid' 
            ? 'bg-green-100/80' 
            : isOverdue 
              ? 'bg-red-100/80' 
              : 'bg-slate-100/80'
        }`}>
          <span className={`text-sm font-semibold ${
            bill.status === 'paid' 
              ? 'text-green-700' 
              : isOverdue 
                ? 'text-red-700' 
                : 'text-slate-600'
          }`} data-testid="bill-card-status">
            {t(bill.status)}
          </span>
          <ArrowRight className={`h-4 w-4 ${
            bill.status === 'paid' ? 'text-green-600' : 'text-slate-400'
          } group-hover:translate-x-1 transition-transform`} />
        </div>
      </div>
    </Card>
  );
}